import React, { useRef } from 'react';
import { motion as Motion, useScroll, useTransform } from 'framer-motion';
import { CropMarks, RegistrationMark, IndexTag, VerticalLabel, FlowChain } from '../components/swiss/SwissKit';

/* Pinned, scroll-driven process section. Lifted out of LandingPage so the
   Prototyping tab can render the identical component: importing it back from
   LandingPage would be a module cycle, since LandingPage imports that page. */

// A single pinned step panel. Cross-fades/slides against neighbors purely
// from the shared scrollYProgress of the tall parent track — no per-panel
// scroll listeners, no viewport-trigger jitter.
const ProcessPinnedStep = ({ p, index, total, scrollYProgress, color, t }) => {
  const seg = 1 / total;
  const start = index * seg;
  const end = start + seg;
  const inAt = start + seg * 0.18;
  const outAt = end - seg * 0.18;

  const opacity = useTransform(
    scrollYProgress,
    [start, inAt, outAt, end],
    [index === 0 ? 1 : 0, 1, 1, index === total - 1 ? 1 : 0]
  );
  const y = useTransform(scrollYProgress, [start, inAt], [index === 0 ? 0 : 48, 0]);
  const scale = useTransform(scrollYProgress, [outAt, end], [1, index === total - 1 ? 1 : 0.96]);
  const bgY = useTransform(scrollYProgress, [start, end], [40, -40]);

  return (
    <Motion.div style={{ opacity, scale }} className="absolute inset-0 flex items-center pointer-events-none">
      <div className="w-full px-[8vw] flex flex-col lg:flex-row items-center pointer-events-auto">
        {/* Mobile: lift the big step number so it starts behind the step
            label and title instead of sitting under the description. */}
        <div className="absolute md:relative lg:w-[45%] py-20 pointer-events-none max-md:-translate-y-[38vw]">
          <Motion.div style={{ y: bgY }} className="relative">
            <span className="text-[60vw] md:text-[30vw] font-black text-text-color/[0.05] leading-none select-none block">
              {p.step}
            </span>
            <div className="absolute top-1/2 left-0 w-32 h-1 rounded-full hidden md:block" style={{ backgroundColor: color }} />
          </Motion.div>
        </div>

        <Motion.div style={{ y }} className="w-full lg:w-[85%] lg:-ml-[20%] relative z-10 gpu-accelerated max-w-7xl">
          <div className="flex items-center gap-4 mb-6">
            <h6 style={{ color }}>{t.common.step} {p.step}</h6>
            <FlowChain steps={['IN', 'PROCESS', 'OUT']} color={color} className="hidden md:flex opacity-60" />
          </div>
          <h3 className="font-black uppercase leading-[0.85] mb-10 tracking-tighter text-text-color perspective-1000 audiowide-regular">
            {p.title}
          </h3>
          <p className="text-text-muted font-light leading-relaxed max-w-4xl">
            {p.text}
          </p>
        </Motion.div>
      </div>
    </Motion.div>
  );
};

// Swiss progress marker — a small tick that fills/highlights while its step
// is the active one, dims otherwise. Reads the same shared scroll progress.
const ProcessProgressMark = ({ index, total, scrollYProgress, color }) => {
  const seg = 1 / total;
  const start = index * seg;
  const end = start + seg;
  const opacity = useTransform(scrollYProgress, [start, start + seg * 0.2, end - seg * 0.2, end], [0.25, 1, 1, 0.25]);
  const height = useTransform(scrollYProgress, [start, start + seg * 0.2, end - seg * 0.2, end], [16, 32, 32, 16]);

  return (
    <div className="flex flex-col items-end gap-2">
      <span className="font-mono-swiss text-[9px] tabular-nums" style={{ opacity }}>
        {String(index + 1).padStart(2, '0')}
      </span>
      <Motion.div style={{ height, backgroundColor: color, opacity }} className="w-[3px] rounded-full" />
    </div>
  );
};

const ProcessPinnedSection = ({ steps, color, t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const total = steps.length;

  return (
    <div ref={ref} className="relative" style={{ height: `${total * 100}vh` }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden gpu-accelerated">
        {/* Frame corners (all four now) + left index/vertical label — the
            right side stays clear up top for the progress markers below. */}
        <div className="hidden md:block absolute inset-0 z-10 pointer-events-none">
          <CropMarks insetTop={8} insetBottom={8} insetX={8} color={color} />
          <div className="absolute top-24 left-6 flex items-center gap-4">
            <RegistrationMark size={16} style={{ color }} className="opacity-70" />
            <IndexTag code="P" num="006" color={color} />
          </div>
          <VerticalLabel text="CRE8NOW / SYSTEM" side="left" offset={24} className="!top-1/2" />
          <VerticalLabel text="MODE / WORKFLOW" side="right" offset={24} className="!top-1/2" />
        </div>

        {/* Swiss progress markers — bottom, centered, visible on every breakpoint */}
        <div className="flex absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 z-20 items-end gap-2 md:gap-3">
          {steps.map((s, i) => (
            <ProcessProgressMark key={s.step} index={i} total={total} scrollYProgress={scrollYProgress} color={color} />
          ))}
        </div>

        {steps.map((p, i) => (
          <ProcessPinnedStep key={p.step} p={p} index={i} total={total} scrollYProgress={scrollYProgress} color={color} t={t} />
        ))}
      </div>
    </div>
  );
};

export default ProcessPinnedSection;
